/**
 * AgentChatInspector — Phase 6a-4.
 *
 * Edits an `agent_chat` node — multi-turn conversation with a deployed
 * agent, optionally executing a specific skill, with upstream
 * variables interpolated into the prompt.
 *
 * Config shape (source: soarNodes.js:3478):
 *
 *   agentId          UUID         deployed agent to converse with
 *   selectedSkill    string       optional skill id (agent auto-picks
 *                                  one when blank)
 *   promptTemplate   string       prompt body with ${var} placeholders
 *   maxTurns         number       conv turn cap (default 5)
 *   contextSize      number       trailing message window (default 10)
 *   outputVariable   string       downstream binding name (default
 *                                  'agent_response')
 *
 * Data sources:
 *   - PlatformContext.agents  — deployed agent list, already loaded by
 *                                the app shell.
 *
 * Spread-preservation contract per useCanvas:1098.
 */

import React, { useMemo } from 'react';
import { usePlatform } from '../../../contexts/PlatformContext';

export default function AgentChatInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const platform = useSafePlatform();
  const agents = platform?.agents || [];

  const agentId         = cfg.agentId || '';
  const selectedSkill   = cfg.selectedSkill || '';
  const promptTemplate  = cfg.promptTemplate || '';
  const maxTurns        = cfg.maxTurns ?? '';
  const contextSize     = cfg.contextSize ?? '';
  const outputVariable  = cfg.outputVariable || '';

  const agentOptions = useMemo(() => {
    if (!Array.isArray(agents) || agents.length === 0) return [];
    return agents
      .filter((a) => a && a.id)
      .map((a) => ({
        value: a.id,
        label: a.name || a.id,
        status: a.status || '',
        isExternal: !!a.isExternal || !!a.externalRegistrationId,
        skills: Array.isArray(a.skills) ? a.skills : [],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [agents]);

  const selectedAgent = useMemo(
    () => agentOptions.find((a) => a.value === agentId) || null,
    [agentOptions, agentId],
  );

  const skillOptions = useMemo(() => {
    if (!selectedAgent || !selectedAgent.skills.length) return [];
    return selectedAgent.skills.map((s) => ({
      value: s.id || s.name,
      label: s.name || s.id,
      published: s.published !== false,
    }));
  }, [selectedAgent]);

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  // Switching agents wipes the now-stale skill pick (the new agent may
  // not expose the previously selected skill id).
  const pickAgent = (id) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    if (id === agentId) return;
    onUpdate(node.id, {
      config: { ...cfg, agentId: id, selectedSkill: '' },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup
        label="Agent"
        hint={agentOptions.length === 0
          ? 'No agents loaded. Deploy an agent on the Agents page first.'
          : 'Pick a deployed agent for this conversation.'}
      >
        <select
          value={agentId}
          onChange={(e) => pickAgent(e.target.value)}
          disabled={readOnly || agentOptions.length === 0}
          style={selectStyle}
        >
          <option value="">(none)</option>
          {agentOptions.map((a) => (
            <option key={a.value} value={a.value}>
              {a.isExternal ? '[ext] ' : ''}{a.label}
              {a.status && a.status !== 'ACTIVE' ? ` — ${a.status}` : ''}
            </option>
          ))}
        </select>
      </FieldGroup>

      <FieldGroup
        label="Execute Skill"
        hint={!agentId
          ? 'Pick an agent first.'
          : (skillOptions.length === 0
            ? 'Agent exposes no skills — leave blank to let the agent decide.'
            : 'Optional — leave blank for agent-driven skill selection.')}
      >
        <select
          value={selectedSkill}
          onChange={(e) => setField('selectedSkill', e.target.value)}
          disabled={readOnly || !agentId || skillOptions.length === 0}
          style={selectStyle}
        >
          <option value="">(agent decides)</option>
          {skillOptions.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}{s.published === false ? ' (draft)' : ''}
            </option>
          ))}
          {selectedSkill && !skillOptions.find((s) => s.value === selectedSkill) && (
            <option value={selectedSkill}>{selectedSkill} (legacy)</option>
          )}
        </select>
      </FieldGroup>

      <FieldGroup
        label="Prompt Template"
        hint="Use ${variableName} placeholders to reference upstream-node outputs."
      >
        <textarea
          value={promptTemplate}
          onChange={(e) => setField('promptTemplate', e.target.value)}
          placeholder="Analyze ${upstream_data} and recommend next steps."
          disabled={readOnly}
          rows={6}
          spellCheck={false}
          style={{
            ...inputStyle,
            fontFamily: 'SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            lineHeight: 1.4,
            resize: 'vertical',
          }}
        />
      </FieldGroup>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FieldGroup label="Max Turns" hint="Conv turn cap (default 5).">
          <input
            type="number"
            min="1"
            step="1"
            value={maxTurns}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') return setField('maxTurns', undefined);
              const n = Number(v);
              if (Number.isFinite(n)) setField('maxTurns', n);
            }}
            placeholder="5"
            disabled={readOnly}
            style={inputStyle}
          />
        </FieldGroup>

        <FieldGroup label="Context Window" hint="Trailing messages (default 10).">
          <input
            type="number"
            min="0"
            step="1"
            value={contextSize}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '') return setField('contextSize', undefined);
              const n = Number(v);
              if (Number.isFinite(n)) setField('contextSize', n);
            }}
            placeholder="10"
            disabled={readOnly}
            style={inputStyle}
          />
        </FieldGroup>
      </div>

      <FieldGroup
        label="Output Variable Name"
        hint="Bind this node's output for downstream ${name} references."
      >
        <input
          type="text"
          value={outputVariable}
          onChange={(e) => setField('outputVariable', e.target.value)}
          placeholder="agent_response"
          disabled={readOnly}
          autoComplete="off"
          spellCheck={false}
          style={inputStyle}
        />
      </FieldGroup>
    </div>
  );
}

/* ───── helpers ───────────────────────────────────────────────────── */

function useSafePlatform() {
  try { return usePlatform(); } catch (_) { return null; }
}

/* ───── shared field UI ───────────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};

const selectStyle = { ...inputStyle, appearance: 'auto' };
